"use strict";
/**
 * Helpers to configure TaskAction packs (flowKind, timeStyle, …)
 * without rewriting every row by hand.
 *
 * Today packs live in TypeScript only (defaultActions1, actions2, bike, reuse).
 * DB action_def / person packs are the later target (person-config-packs.md).
 *
 * Usage:
 *   export const actions2 = configureActionPack(raw, [
 *     byId({ 500100: { flowKind: 'I' }, ... }),
 *     whereTaskGroup('Logistics', { flowKind: 'L', timeStyle: 'stamp' }),
 *     whereCaption(/Idea|Note/, { flowKind: 'I' }),
 *   ]);
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.summarizeFlowKinds = exports.configureActionPack = exports.defaultFlowWhenMissing = exports.whereCaption = exports.whereTopicGroup = exports.whereTaskGroup = exports.where = exports.byId = exports.applyFlow = void 0;
/** Merge flow fields onto a single action (does not mutate original). */
function applyFlow(action, patch) {
    var next = __assign(__assign(__assign({}, action), pickDefined(patch)), { auditRow: __assign({}, action.auditRow) });
    // Keep auditRow in sync so postTaskAction / server always see flow even if top-level is stripped
    if (patch.flowKind !== undefined)
        next.auditRow.flowKind = patch.flowKind;
    if (patch.timeStyle !== undefined)
        next.auditRow.timeStyle = patch.timeStyle;
    if (patch.sealScope !== undefined)
        next.auditRow.sealScope = patch.sealScope;
    if (patch.closesPreviousT !== undefined)
        next.auditRow.closesPreviousT = patch.closesPreviousT;
    if (patch.opensFocus !== undefined)
        next.auditRow.opensFocus = patch.opensFocus;
    if (patch.planningKind !== undefined)
        next.auditRow.planningKind = patch.planningKind;
    if (patch.createsSchedule !== undefined) {
        next.createsSchedule = patch.createsSchedule;
        next.auditRow.schedule = patch.createsSchedule ? true : false;
    }
    return next;
}
exports.applyFlow = applyFlow;
function pickDefined(patch) {
    var out = {};
    Object.keys(patch).forEach(function (k) {
        if (patch[k] !== undefined) {
            out[k] = patch[k];
        }
    });
    return out;
}
/** Apply patch to every action whose id is in the map. */
function byId(map) {
    return function (actions) {
        return actions.map(function (a) {
            var patch = map[a.id];
            return patch ? applyFlow(a, patch) : a;
        });
    };
}
exports.byId = byId;
/** Apply patch where predicate is true (later steps win if you re-apply). */
function where(pred, patch) {
    return function (actions) { return actions.map(function (a) { return (pred(a) ? applyFlow(a, patch) : a); }); };
}
exports.where = where;
function whereTaskGroup(taskGroup, patch) {
    return where(function (a) { var _a; return (((_a = a.auditRow) === null || _a === void 0 ? void 0 : _a.taskGroup) || a.taskGroup) === taskGroup; }, patch);
}
exports.whereTaskGroup = whereTaskGroup;
function whereTopicGroup(topicGroup, patch) {
    return where(function (a) { var _a; return (((_a = a.auditRow) === null || _a === void 0 ? void 0 : _a.topicGroup) || a.topicGroup) === topicGroup; }, patch);
}
exports.whereTopicGroup = whereTopicGroup;
function whereCaption(match, patch) {
    return where(function (a) {
        var c = a.caption || '';
        return typeof match === 'string' ? c === match : match.test(c);
    }, patch);
}
exports.whereCaption = whereCaption;
/** Only fill flowKind when missing (does not override hand-set values). */
function defaultFlowWhenMissing(patch) {
    return function (actions) {
        return actions.map(function (a) {
            var _a;
            if (a.flowKind != null || ((_a = a.auditRow) === null || _a === void 0 ? void 0 : _a.flowKind) != null) {
                return a;
            }
            return applyFlow(a, patch);
        });
    };
}
exports.defaultFlowWhenMissing = defaultFlowWhenMissing;
/**
 * Run a stack of configuration steps left → right.
 * Later steps override earlier ones on the same action.
 */
function configureActionPack(actions, steps) {
    return steps.reduce(function (acc, step) { return step(acc); }, actions.map(function (a) { return (__assign(__assign({}, a), { auditRow: __assign({}, a.auditRow) })); }));
}
exports.configureActionPack = configureActionPack;
/** Human-readable summary for debugging / tests */
function summarizeFlowKinds(actions) {
    var _a, _b, _c;
    var counts = {};
    for (var _i = 0, actions_1 = actions; _i < actions_1.length; _i++) {
        var a = actions_1[_i];
        var k = (_c = (_a = a.flowKind) !== null && _a !== void 0 ? _a : (_b = a.auditRow) === null || _b === void 0 ? void 0 : _b.flowKind) !== null && _c !== void 0 ? _c : '(unset→I)';
        counts[k] = (counts[k] || 0) + 1;
    }
    return counts;
}
exports.summarizeFlowKinds = summarizeFlowKinds;
